import { useUncontrolledError } from "../../utils/useUncontrolledError";
import UncontrolledInputCreatableSelect from "../inputs/UncontrolledInputCreatableSelect";
import { EnhancedMappingInput } from "./EnhancedMappingInput";
import { FieldDescriptionPopup } from "./FieldDescriptionPopup";
import MappingFieldList from "./MappingFieldList";
import MappingValueList from "./MappingValueList";
import { augmentMappings } from "./augmentUtils";
import { switchMappingType } from "./switchMappingType";
import { getBuiltInFields, getBuiltInFieldsClientMapping, getFullFieldName, mappingFieldIsValid } from "@avenueops/frontend-shared";
import { fieldIsRequired } from "@avenueops/frontend-shared/dist/mappings/mapping-utils/fieldIsRequired";
import {
  ClientMappingApiType,
  ClientMappingField,
  ClientMappingValue,
  defaultClientMappingField,
  ClientMappingFieldWithPrefix,
  MERGE_ASANA_REMOTE_FIELDS,
} from "@avenueops/shared-types";
import { Button } from "@avenueops/web-components";
import _ from "lodash";
import React, { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import Skeleton from "react-loading-skeleton";
import { trpc2 } from "utils/trpc";

interface Props {
  update: (_: ClientMappingField) => void;
  selectedIntegrationId: string;
  integrationData: any;
  remove: () => void;
  field: ClientMappingField;
  mappingPrefix: string;
  clientMappingApiType: ClientMappingApiType;
  isFirst: boolean;
  level: number;
  prefix: string;
  notificationPrefix?: string;
}

export default function MappingField({
  selectedIntegrationId,
  integrationData,
  update,
  remove,
  mappingPrefix,
  clientMappingApiType,
  isFirst,
  level,
  field,
  prefix,
  notificationPrefix,
}: Props) {
  const { watch, setError, clearErrors } = useFormContext();

  const recipientValue = notificationPrefix ? watch(`${notificationPrefix}.recipientValue`) : undefined;
  const fullFieldName = getFullFieldName(mappingPrefix, field?.name);

  const augmentedOptions: ClientMappingFieldWithPrefix[] | undefined = augmentMappings(
    clientMappingApiType,
    fullFieldName,
    selectedIntegrationId,
    recipientValue
  );

  const options = getBuiltInFields(mappingPrefix, clientMappingApiType, "OPTIONAL", augmentedOptions);

  // Initialize this as a state variable so that if you accidentally name a not required field
  // with a required field's name it doesn't become required
  // because then you won't be able to delete it.
  const [isRequired, setIsRequired] = useState<boolean | undefined>(fieldIsRequired(field.name, mappingPrefix, clientMappingApiType));

  useEffect(() => {
    setIsRequired(fieldIsRequired(field.name, mappingPrefix, clientMappingApiType));
  }, [options]);

  // This ensures that if the user has created nested objects w/o names, we won't embed the first layer's required fields in it
  const requiredFieldsPrefix = mappingPrefix === "" && level > 1 ? "_" : "";
  const requiredFieldsMapping = getBuiltInFieldsClientMapping(`${requiredFieldsPrefix}${fullFieldName}`, clientMappingApiType, "REQUIRED");
  useEffect(() => {
    // If the field name is in options just get the type of that option and
    // don't allow the user to change it
    const fieldType = options.get(field.name) ? options?.get(field.name)?.type : field?.type;

    // Set fieldType if it's different from the current value
    if (fieldType != field.type) {
      update({ ...field, type: fieldType } as ClientMappingField);
    }
    if (field.type === "OBJECT" && field.fields === undefined) {
      update({ ...field, fields: requiredFieldsMapping.fields });
    }
  }, [field]);

  const error = useUncontrolledError<ClientMappingField>(mappingFieldIsValid, field);

  useEffect(() => {
    if (error) {
      setError(prefix, { type: "manual", message: "This field is invalid" });
    } else if (!error) {
      clearErrors(prefix);
    }
  }, [error]);

  return (
    <div className={`flex flex-col items-start w-full pb-5`}>
      <div className={`flex flex-col xl:flex-row items-start w-full pb-5`}>
        <div className="flex flex-row flex-1 w-full pb-3 xl:pb-0">
          <div className="flex-1">
            <UncontrolledInputCreatableSelect
              disabled={isRequired}
              isClearable={false}
              options={Array.from(options.keys()).map((optionName) => ({ value: optionName as string, label: optionName as string }))}
              error={error}
              val={field.name}
              onChange={(name) => {
                update(options.get(name) as ClientMappingField);
              }}
              onCreateOption={(name: string) => {
                const createdField = { ...defaultClientMappingField };
                createdField.name = name;
                update(createdField);
              }}
              title={`${fullFieldName != "" ? fullFieldName : "Field"}`}
              placeholder="Field name..."
              secondaryElement={
                fullFieldName != "" ? <FieldDescriptionPopup fullFieldName={fullFieldName} clientMappingApiType={clientMappingApiType} /> : null
              }
            />
          </div>

          <div className={"pt-6 pl-3"}>
            <Button disabled={isRequired || options.has(field.name)} onClick={() => switchMappingType<ClientMappingField>(update, field)}>
              {field.type === "VALUE" ? "=" : field.type === "LIST" ? "[]" : "{}"}
            </Button>
          </div>
        </div>

        <div className="flex flex-row w-full flex-1">
          <div className="flex-1">
            <div className="xl:pl-3">
              <EnhancedMappingInput
                selectedIntegrationId={selectedIntegrationId}
                integrationData={integrationData}
                update={update}
                mappingPrefix={mappingPrefix}
                clientMappingApiType={clientMappingApiType}
                isFirst={isFirst}
                field={field}
                prefix={prefix}
                notificationPrefix={notificationPrefix}
              />
            </div>
          </div>
          <div className="pt-6 pl-3">
            <Button onClick={remove} type="DANGER_SECONDARY" disabled={isRequired}>
              -
            </Button>
          </div>
        </div>
      </div>

      {field.type === "OBJECT" && (
        <div className="flex flex-row w-full">
          <div className="border-l border-cool-gray-300 pr-5 mb-5" />
          <MappingFieldList
            integrationData={integrationData}
            selectedIntegrationId={selectedIntegrationId}
            prefix={`${prefix}.fields`}
            notificationPrefix={notificationPrefix}
            isFirst={false}
            mappingPrefix={fullFieldName}
            clientMappingApiType={clientMappingApiType}
            level={level + 1}
            fields={field.fields}
            update={(fields: ClientMappingField[]) => update({ ...field, fields })}
          />
        </div>
      )}
      {field.type === "LIST" && (
        <div className="flex flex-row w-full">
          <div className="border-l border-cool-gray-300 pr-5 mb-5" />
          <MappingValueList
            integrationData={integrationData}
            selectedIntegrationId={selectedIntegrationId}
            prefix={`${prefix}.values`}
            values={field.values}
            update={(values: ClientMappingValue[]) => update({ ...field, values })}
            isFirst={false}
            mappingPrefix={fullFieldName}
            clientMappingApiType={clientMappingApiType}
            level={level + 1}
          />
        </div>
      )}
    </div>
  );
}
